import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useSortable } from '@/hooks/useSortable'

interface Board {
  id: number
  name: string
  slug: string
  color: string | null
  project: { id: number; name: string }
  task_count: number
  member_count: number
  created_at: string
}

interface ProjectOption {
  id: number
  name: string
  slug: string
}

interface AgentOption {
  id: number
  username: string
}

interface Props {
  boards: Board[]
  projects: ProjectOption[]
  agents: AgentOption[]
  filters: {
    project_id: number | null
    agent_id: number | null
  }
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'project.name', label: 'Project' },
  { value: 'task_count', label: 'Tasks' },
  { value: 'member_count', label: 'Members' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ boards, projects, agents, filters }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(boards, 'name')
  const [deleteTarget, setDeleteTarget] = useState<Board | null>(null)

  function handleDelete() {
    if (deleteTarget) {
      router.delete(`/manage/boards/${deleteTarget.id}`)
      setDeleteTarget(null)
    }
  }

  function handleFilterChange(key: string, value: number | '') {
    const params: Record<string, string> = {}
    const currentFilters = { ...filters, [key]: value || null }
    if (currentFilters.project_id) params.project_id = String(currentFilters.project_id)
    if (currentFilters.agent_id) params.agent_id = String(currentFilters.agent_id)
    router.get('/manage/boards', params, { preserveState: true })
  }

  return (
    <SidebarLayout
      section="manage"
      title="Boards"
      action={
        <Button component={Link} href="/manage/boards/new" variant="contained">
          New board
        </Button>
      }
    >
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filters.project_id || ''}
            label="Project"
            onChange={e => handleFilterChange('project_id', e.target.value as number | '')}
          >
            <MenuItem value="">All projects</MenuItem>
            {projects.map(p => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Agent</InputLabel>
          <Select
            value={filters.agent_id || ''}
            label="Agent"
            onChange={e => handleFilterChange('agent_id', e.target.value as number | '')}
          >
            <MenuItem value="">All agents</MenuItem>
            {agents.map(a => (
              <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={orderBy}
            label="Sort by"
            onChange={e => handleSort(e.target.value)}
          >
            {SORT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton size="small" onClick={() => handleSort(orderBy)}>
          {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        </IconButton>
      </Box>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No boards found. Create one to get started.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((board) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={board.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ height: 4, backgroundColor: board.color || '#6B7280' }} />
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {board.name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {board.project.name}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                    <Chip label={`${board.task_count} tasks`} size="small" variant="outlined" />
                    <Chip label={`${board.member_count} members`} size="small" variant="outlined" />
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                    Created {new Date(board.created_at).toLocaleDateString()}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Button component={Link} href={`/manage/boards/${board.id}/edit`} size="small">
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => setDeleteTarget(board)}>
                      Delete
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete board"
        message={`Are you sure you want to delete board '${deleteTarget?.name}'? All tasks in this board will also be deleted.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </SidebarLayout>
  )
}
